// Resident Portal — ResidentValetFeedback component
// Gated feedback form for completed valet service events.
//
// Gate contract:
// - If eligibility is loading: render nothing
// - If not eligible: render nothing (no disabled state — just absent)
// - If eligible: render the feedback form
//
// On successful submission: form is hidden for the lifetime of this component
// instance (session-level, tied to serviceId + locationId).

import { useState } from "react";
import { functions, auth } from "../firebase";
import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import {
  useResidentFeedbackEligibility,
  type ResidentFeedbackEligibilityParams,
} from "../hooks/useResidentFeedbackEligibility";

// ── Types ──────────────────────────────────────────────────────────────────

const FEEDBACK_TAGS = [
  "friendly",
  "fast",
  "careful",
  "professional",
  "attentive",
] as const;

type FeedbackTag = (typeof FEEDBACK_TAGS)[number];

export interface ResidentValetFeedbackProps {
  locationId: string;
  residentId: string;
  serviceId: string;           // checkinId or valetRequestId
  serviceType?: string;        // "retrieval" | "checkin" — defaults to "retrieval"
  serviceStatus?: string;
  serviceCompletedAt?: string | null;
  alreadySubmitted?: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function ResidentValetFeedback(props: ResidentValetFeedbackProps) {
  const {
    locationId,
    residentId,
    serviceId,
    serviceType = "retrieval",
    serviceStatus,
    serviceCompletedAt,
    alreadySubmitted = false,
  } = props;

  const eligibilityParams: ResidentFeedbackEligibilityParams = {
    locationId,
    serviceId,
    serviceType,
    serviceStatus,
    serviceCompletedAt,
    alreadySubmitted,
  };

  const { eligible, loading, flags } = useResidentFeedbackEligibility(eligibilityParams);

  // If loading or not eligible: render nothing
  if (loading || !eligible || !flags) return null;

  return (
    <FeedbackForm
      locationId={locationId}
      residentId={residentId}
      serviceId={serviceId}
      serviceType={serviceType}
      flags={flags}
    />
  );
}

// ── Internal FeedbackForm ──────────────────────────────────────────────────

interface FeedbackFormProps {
  locationId: string;
  residentId: string;
  serviceId: string;
  serviceType: string;
  flags: {
    allowTags: boolean;
    allowComment: boolean;
  };
}

type SubmitState = "idle" | "submitting" | "success" | "error";

function FeedbackForm({
  locationId,
  residentId,
  serviceId,
  serviceType,
  flags,
}: FeedbackFormProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredStar, setHoveredStar] = useState<number>(0);
  const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>([]);
  const [comment, setComment] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Once submitted successfully, hide the form permanently for this instance
  if (submitState === "success") {
    return <FeedbackSuccess />;
  }

  const handleTagToggle = (tag: FeedbackTag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (rating === 0) return;

    const user = auth.currentUser;
    if (!user) {
      setErrorMessage("You must be logged in to submit feedback.");
      return;
    }

    setSubmitState("submitting");
    setErrorMessage(null);

    try {
      await user.getIdToken(true);

      const payload: Record<string, unknown> = {
        locationId,
        residentId,
        rating,
        serviceType,
        source: "portal",
      };

      // Map serviceId to correct field based on serviceType
      if (serviceType === "checkin" || serviceType === "retrieval") {
        payload["checkinId"] = serviceId;
      } else {
        payload["valetRequestId"] = serviceId;
      }

      if (flags.allowTags && selectedTags.length > 0) {
        payload["tags"] = selectedTags;
      }

      if (flags.allowComment && comment.trim().length > 0) {
        payload["comment"] = comment.trim();
      }

      const submitFn = httpsCallable(functions, "submitResidentValetFeedback");
      const result: HttpsCallableResult<any> = await submitFn(payload);

      const data = result?.data;
      if (!data?.ok) {
        const msg = data?.error?.message ?? "Unable to submit feedback. Please try again.";
        throw new Error(msg);
      }

      console.log("FEEDBACK_SUBMIT_SUCCESS", { locationId, serviceId, rating });
      setSubmitState("success");
    } catch (err: any) {
      console.error("FEEDBACK_SUBMIT_ERROR", err);
      setSubmitState("error");
      setErrorMessage(err?.message ?? "Something went wrong. Please try again.");
    }
  };

  const displayRating = hoveredStar > 0 ? hoveredStar : rating;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <span style={styles.headerText}>How was your service?</span>
      </div>

      {/* Star Rating */}
      <div style={styles.starsRow}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            style={{
              ...styles.starButton,
              color: star <= displayRating ? "#D4A843" : "rgba(255,255,255,0.2)",
            }}
            onClick={() => setRating(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(0)}
            aria-label={`Rate ${star} star${star !== 1 ? "s" : ""}`}
            disabled={submitState === "submitting"}
          >
            ★
          </button>
        ))}
      </div>

      {/* Tags */}
      {flags.allowTags && rating > 0 && (
        <div style={styles.tagsRow}>
          {FEEDBACK_TAGS.map((tag) => (
            <button
              key={tag}
              onClick={() => handleTagToggle(tag)}
              disabled={submitState === "submitting"}
              style={{
                ...styles.tagChip,
                ...(selectedTags.includes(tag) ? styles.tagChipSelected : {}),
              }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Comment */}
      {flags.allowComment && rating > 0 && (
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment (optional)"
          disabled={submitState === "submitting"}
          maxLength={500}
          style={styles.commentInput}
          rows={3}
        />
      )}

      {/* Error */}
      {submitState === "error" && errorMessage && (
        <p style={styles.errorText}>{errorMessage}</p>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={rating === 0 || submitState === "submitting"}
        style={{
          ...styles.submitButton,
          ...(rating === 0 || submitState === "submitting"
            ? styles.submitButtonDisabled
            : {}),
        }}
      >
        {submitState === "submitting" ? "Submitting..." : "Submit Feedback"}
      </button>
    </div>
  );
}

// ── Success state ──────────────────────────────────────────────────────────

function FeedbackSuccess() {
  return (
    <div style={styles.successCard}>
      <p style={styles.successText}>Thank you for your feedback</p>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: "#1A1A1A",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  header: {
    marginBottom: "4px",
  },
  headerText: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#E0E0E0",
    letterSpacing: "0.01em",
  },
  starsRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  starButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "32px",
    padding: "2px 4px",
    lineHeight: 1,
    transition: "color 0.1s ease",
  },
  tagsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
  },
  tagChip: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "20px",
    color: "#B0B0B0",
    fontSize: "13px",
    fontWeight: 500,
    padding: "6px 14px",
    cursor: "pointer",
    textTransform: "capitalize" as const,
    transition: "all 0.15s ease",
  },
  tagChipSelected: {
    background: "rgba(212, 168, 67, 0.12)",
    border: "1px solid rgba(212, 168, 67, 0.5)",
    color: "#D4A843",
  },
  commentInput: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "8px",
    color: "#E0E0E0",
    fontSize: "14px",
    lineHeight: 1.5,
    padding: "10px 14px",
    resize: "none" as const,
    width: "100%",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box" as const,
  },
  errorText: {
    color: "#E53935",
    fontSize: "13px",
    margin: 0,
  },
  submitButton: {
    background: "#D4A843",
    border: "none",
    borderRadius: "8px",
    color: "#0C0C0C",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 600,
    height: "44px",
    transition: "opacity 0.15s ease",
    width: "100%",
  },
  submitButtonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  successCard: {
    background: "rgba(5, 150, 105, 0.08)",
    border: "1px solid rgba(5, 150, 105, 0.2)",
    borderRadius: "12px",
    padding: "20px 24px",
    textAlign: "center" as const,
  },
  successText: {
    color: "#34D399",
    fontSize: "15px",
    fontWeight: 500,
    margin: 0,
  },
};
